// The attachment list and upload control, shared by the requester and staff views (P12). Scan
// state is shown honestly: a file is downloadable only once the server says it is clean, and the
// link is requested fresh each time because it expires in a minute.

import type { Attachment, Me } from "@hoffman-support/contracts";
import { useEffect, useState } from "react";
import { ApiError } from "../api";
import { downloadSlot, listAttachments, uploadAttachment } from "../attachments-api";

const STATE_TEXT: Record<Attachment["scanState"], string> = {
  pending: "upload not finished",
  scanning: "being scanned",
  clean: "ready",
  infected: "not accepted: failed the malware scan",
  failed: "not accepted",
};

export function Attachments({
  me,
  requestId,
  staff = false,
  canUpload,
}: {
  me: Me;
  requestId: string;
  staff?: boolean;
  canUpload: boolean;
}) {
  const [items, setItems] = useState<Attachment[]>([]);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      setItems(await listAttachments(requestId, staff));
    } catch {
      setNotice("Attachments could not be loaded.");
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load is defined per mount and reads only stable props
  useEffect(() => {
    void load();
  }, [requestId]);

  async function pick(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    setNotice(`Uploading ${file.name}…`);
    try {
      const uploaded = await uploadAttachment(me, requestId, file, staff);
      setNotice(`${uploaded.filename} uploaded; it will be available once it has been scanned.`);
      await load();
    } catch (error) {
      setNotice(
        error instanceof ApiError ? error.failure.message : "The upload failed; try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function open(attachment: Attachment) {
    try {
      const slot = await downloadSlot(requestId, attachment.id, staff);
      window.open(slot.url, "_blank", "noopener");
    } catch (error) {
      setNotice(
        error instanceof ApiError ? error.failure.message : "The file could not be opened.",
      );
    }
  }

  return (
    <section aria-labelledby="attachments-heading">
      <h3 id="attachments-heading">Attachments</h3>
      {items.length === 0 ? <p>No files attached.</p> : null}
      <ul>
        {items.map((a) => (
          <li key={a.id}>
            {a.downloadable ? (
              <button
                type="button"
                onClick={() => void open(a)}
                style={{
                  padding: 0,
                  border: 0,
                  background: "none",
                  color: "#06c",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {a.filename}
              </button>
            ) : (
              <span>{a.filename}</span>
            )}{" "}
            <span style={{ color: "#555", fontSize: "0.85rem" }}>
              ({Math.max(1, Math.round(a.size / 1024))} KB, {STATE_TEXT[a.scanState]}
              {a.rejectedReason && a.scanState === "failed" ? `: ${a.rejectedReason}` : ""})
            </span>
          </li>
        ))}
      </ul>
      {canUpload ? (
        <p>
          <label>
            Add a screenshot or file (PNG, JPEG, GIF, WebP, PDF, or text, up to 25 MB){" "}
            <input
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain"
              onChange={(e) => void pick(e)}
              disabled={busy}
            />
          </label>
        </p>
      ) : null}
      <p role="status" aria-live="polite">
        {notice}
      </p>
    </section>
  );
}
